import React, { useState, ReactElement } from 'react';
import { t, defineMessages } from '@sm/intl';
import { createURL } from '@sm/utils';
import { Box } from '@wds/box';
import { IconPlus } from '@wds/icons';
import { List } from '@wds/list';
import { Responsive } from '@wds/responsive';
import { Sheet } from '@wds/sheet';
import { useHeaderTheme } from '../../theme';
import { Logo } from '../logo';
import { VariantComponent } from '../../variants';
import { Brand } from '../../types';
import { SubMenuItem } from './SubMenuItem';
import { MobileMenuButton } from './MobileMenuButton';
import { signOut } from '../../../config/MobileLinkInfo.js';

import useStyles from './useStyles';

const COPY = defineMessages({
  CREATE_SURVEY_LABEL: {
    id: 'MobileNav.createSurvey',
    defaultMessage: 'Create survey icon',
    description:
      '[Type: Label][Vis.: low] - Create Survey plus button on mobile',
  },
  MENU_ICON_LABEL: {
    id: 'MobileNav.menuIcon',
    defaultMessage: 'Menu icon',
    description:
      '[Type: Label][Vis.: low] - Mobile navigation menu button icon',
  },
  CLOSE_ICON_LABEL: {
    id: 'MobileNav.closeIcon',
    defaultMessage: 'Close menu icon',
    description:
      '[Type: Label][Vis.: low] - Mobile navigation close button icon',
  },
});

export type MobileNavProps = {
  brand: Brand;
  userMenu: VariantComponent;
  navItems: VariantComponent;
  infoMenu: VariantComponent;
  link: string;
};

const MobileNav = ({
  brand,
  userMenu,
  navItems,
  link,
  infoMenu,
}: MobileNavProps): ReactElement => {
  const theme = useHeaderTheme();
  const classes = useStyles({ theme });

  const [showMobileNav, setShowMobileNav] = useState(false);
  const toggleMobileNav = (): void => setShowMobileNav(!showMobileNav);

  return (
    <Responsive maxWidth="xl">
      <>
        <Responsive maxWidth="md">
          <a
            href={createURL('/create/', { ut_source: 'header' })}
            className={classes.createButton}
          >
            <IconPlus size="xl" title={t(COPY.CREATE_SURVEY_LABEL)} />
          </a>
        </Responsive>
        <div className={classes.mobileNavContainer}>
          <Box>
            <MobileMenuButton
              showMobileNav={showMobileNav}
              toggleMobileNav={toggleMobileNav}
            />
            <Logo brand={brand} link={link} />
          </Box>
          <Sheet
            showCloseButton
            onClose={toggleMobileNav}
            show={showMobileNav}
            placement="left"
            offsetTop={48}
          >
            <Box className={classes.mobileNavMenu}>
              <List noRules interactive>
                {userMenu}
                {navItems}
              </List>
              {infoMenu}
              {userMenu && (
                <List interactive>
                  <SubMenuItem {...signOut()} />
                </List>
              )}
            </Box>
          </Sheet>
        </div>
      </>
    </Responsive>
  );
};

export default MobileNav;
export { MobileNav };
